import { useEffect, useState } from 'react';
import { useApiHolder } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder';

/** The slice of a scaffolder task this list reads. */
type ScaffolderTask = {
  id: string;
  status: string;
  createdAt: string;
  spec: {
    templateInfo?: {
      entity?: { metadata?: { name?: string; title?: string } };
    };
  };
};

/**
 * The portal's own half of "what did I ask for": template runs.
 *
 * Control runs (above, in RequestsPage) only exist once a template reaches its
 * `control:*:request` step. A Graph-RAG starter never does, and neither does a
 * service run that failed at a preflight — which is exactly the run somebody
 * needs to find again. The stock Create › Tasks tab used to be the only list of
 * these; with it disabled (D-34), this is where they live.
 */
const TASK_LABEL: Record<string, string> = {
  open: 'Queued',
  processing: 'Running',
  completed: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};
const TASK_COLOUR: Record<string, string> = {
  open: 'var(--ar-fg-3)',
  processing: '#4c8dff',
  completed: '#3fb950',
  failed: '#f85149',
  cancelled: 'var(--ar-fg-3)',
  skipped: 'var(--ar-fg-3)',
};

const useStyles = makeStyles(theme => ({
  heading: {
    fontSize: '0.8125rem',
    fontWeight: 660,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-2)',
    margin: '32px 0 10px',
  },
  list: {
    border: '1px solid var(--ar-border-soft)',
    borderRadius: 8,
    background: 'var(--ar-surface)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    borderTop: '1px solid var(--ar-border-soft)',
    '&:first-child': { borderTop: 'none' },
    textDecoration: 'none',
    '&:hover': { backgroundColor: 'var(--ar-surface-2)' },
  },
  title: { fontWeight: 600, fontSize: '0.9375rem', color: 'var(--ar-fg)' },
  meta: { color: theme.palette.text.secondary, fontSize: '0.8125rem' },
  spacer: { flex: 1 },
  hint: { fontSize: '0.8125rem', color: 'var(--ar-fg-3)' },
}));

export const TemplateRuns = () => {
  const classes = useStyles();
  // Through the holder: the page must still render where the scaffolder API
  // is not registered (tests, a stripped-down app), just without this section.
  const scaffolderApi = useApiHolder().get(scaffolderApiRef);
  const [tasks, setTasks] = useState<ScaffolderTask[]>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!scaffolderApi) return undefined;
    let cancelled = false;
    scaffolderApi
      .listTasks({ filterByOwnership: 'owned', limit: 20 })
      .then(r => !cancelled && setTasks(r.tasks as ScaffolderTask[]))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [scaffolderApi]);

  if (!scaffolderApi) return null;

  return (
    <section aria-label="Template runs">
      <h2 className={classes.heading}>Template runs</h2>
      {failed && (
        <Typography className={classes.hint}>
          Template runs could not be loaded just now.
        </Typography>
      )}
      {tasks?.length === 0 && (
        <Typography className={classes.hint}>
          No template runs yet. Every Deploy a service or starter run shows up
          here, including ones that stopped before reaching Control.
        </Typography>
      )}
      {tasks && tasks.length > 0 && (
        <div className={classes.list}>
          {tasks.map(task => {
            const meta = task.spec.templateInfo?.entity?.metadata;
            return (
              <Link
                key={task.id}
                to={`/create/request/task/${task.id}`}
                className={classes.row}
              >
                <span className={classes.title}>
                  {meta?.title ?? meta?.name ?? 'Template run'}
                </span>
                <span className={classes.meta}>
                  {new Date(task.createdAt).toLocaleString()}
                </span>
                <span className={classes.spacer} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={TASK_LABEL[task.status] ?? task.status}
                  style={{
                    color: TASK_COLOUR[task.status],
                    borderColor: TASK_COLOUR[task.status],
                  }}
                />
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
};
